import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { CustomReportServiceBase } from './custom-report/custom-report-base.service';
import {
    AwardedEstimateClient,
    BoxResultType,
    CustomReportAwardedEstimateView,
    EntityTypeCustomFieldDto,
    GetEstimateAwardedColumnChartValuesResultType,
    GetEstimateAwardedLineChartValuesResultType,
    GetEstimateAwardedMetricsValuesResultType,
    GetEstimateAwardedValuesParameters,
    GetEstimateAwardedValuesResultType,
    ReportResultColumnDto,
    SharedReportDto
} from './reporting-api-proxies';

@Injectable({
    providedIn: 'root'
})
export class AwardedEstimateService extends CustomReportServiceBase<
    AwardedEstimateClient,
    CustomReportAwardedEstimateView
> {
    constructor(protected override client: AwardedEstimateClient) {
        super(client);
    }

    async getEstimateValues(
        parameters: GetEstimateAwardedValuesParameters
    ): Promise<GetEstimateAwardedValuesResultType[]> {
        return await lastValueFrom(this.client.awardedEstimateGetEstimateAwardedValues(parameters));
    }

    async getCustomFieldColumns(organizationIds: number[]): Promise<EntityTypeCustomFieldDto[]> {
        return await lastValueFrom(this.client.awardedEstimateGetCustomFieldColumns(organizationIds));
    }

    async getEstimateBoxesValues(parameters: GetEstimateAwardedValuesParameters): Promise<BoxResultType[]> {
        return await lastValueFrom(this.client.awardedEstimateGetEstimateAwardedBoxesValues(parameters));
    }

    async getEstimateColumnChartValues(
        parameters: GetEstimateAwardedValuesParameters
    ): Promise<GetEstimateAwardedColumnChartValuesResultType[]> {
        return await lastValueFrom(this.client.awardedEstimateGetEstimateAwardedColumnChartValues(parameters));
    }

    async getEstimateMetricsValues(
        parameters: GetEstimateAwardedValuesParameters
    ): Promise<GetEstimateAwardedMetricsValuesResultType> {
        return await lastValueFrom(this.client.awardedEstimateGetEstimateAwardedMetricsValues(parameters));
    }

    async getEstimateLineChartValues(
        parameters: GetEstimateAwardedValuesParameters
    ): Promise<GetEstimateAwardedLineChartValuesResultType[]> {
        return await lastValueFrom(this.client.awardedEstimateGetEstimateAwardedLineChartValues(parameters));
    }

    override async saveCustomReport(body: CustomReportAwardedEstimateView): Promise<CustomReportAwardedEstimateView> {
        if (body.isPublic) return await lastValueFrom(this.client.awardedEstimateSaveCatalogReport(body));
        else return await lastValueFrom(this.client.awardedEstimateSaveCustomReport(body));
    }

    override async getCustomReport(id: number): Promise<CustomReportAwardedEstimateView> {
        return await lastValueFrom(this.client.awardedEstimateGetCustomReport(id));
    }

    override async shareReport(body: CustomReportAwardedEstimateView): Promise<SharedReportDto> {
        return await lastValueFrom(this.client.awardedEstimateCreateSharedReport(body));
    }

    override async getSharedReport(id: string): Promise<CustomReportAwardedEstimateView> {
        return await lastValueFrom(this.client.awardedEstimateGetSharedReport(id));
    }

    override async getResultAvailableColumns(organizationIds: number[]): Promise<ReportResultColumnDto[]> {
        return await lastValueFrom(this.client.awardedEstimateResultAvailableColumns(organizationIds));
    }
}
